import { useState } from "react";
import { clsx } from "clsx";
import { ExternalLink } from "lucide-react";
import {
  useDORAMetrics,
  useProductivityMetrics,
  useBusinessMetrics,
  useMetricTrend,
} from "../hooks/useMetrics";
import {
  useAnalyticsSummary,
  usePRActivity,
  useAnalyticsBuildFailures,
  useAnalyticsHotfixes,
  useAnalyticsZeroReviews,
  useAnalyticsForceMerges,
} from "../hooks/useGitHub";
import PageHeader from "../components/common/PageHeader";
import MetricCard from "../components/common/MetricCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

function trendDirection(
  comparison: Record<string, { changePct?: number | null }> | undefined,
  key: string
): { value: number; direction: "up" | "down" } | undefined {
  const change = comparison?.[key]?.changePct;
  if (change == null) return undefined;
  return { value: Math.abs(change), direction: change >= 0 ? "up" : "down" };
}

export default function ManagementDashboard() {
  const [periodDays, setPeriodDays] = useState(30);
  const {
    data: dora,
    isLoading: doraLoading,
    error: doraError,
    refetch: doraRefetch,
  } = useDORAMetrics(periodDays);
  const { data: productivity } = useProductivityMetrics(periodDays);
  const { data: business } = useBusinessMetrics(periodDays);
  const { data: deployFreqTrend } = useMetricTrend(
    "dora",
    "deployment_frequency",
    6
  );
  const { data: prMergedTrend } = useMetricTrend(
    "productivity",
    "prs_merged",
    6
  );

  const analyticsDays = periodDays <= 7 ? 7 : periodDays <= 30 ? 30 : 90;
  const { data: analytics } = useAnalyticsSummary({ days: analyticsDays });
  const { data: prActivity } = usePRActivity({ days: analyticsDays });
  const { data: buildFailures } = useAnalyticsBuildFailures({ days: analyticsDays });
  const { data: hotfixes } = useAnalyticsHotfixes({ days: analyticsDays });
  const { data: zeroReviews } = useAnalyticsZeroReviews({ days: analyticsDays });
  const { data: forceMerges } = useAnalyticsForceMerges({ days: analyticsDays });

  const isLoading = doraLoading;

  if (isLoading) return <LoadingSpinner message="Loading KPI dashboard..." />;
  if (doraError)
    return (
      <ErrorBanner message="Failed to load metrics" onRetry={doraRefetch} />
    );

  const deployTrendData = (deployFreqTrend?.dataPoints ?? []).map((dp) => ({
    period: format(new Date(dp.periodStart), "MMM"),
    value: dp.value,
  }));

  const prTrendData = (prMergedTrend?.dataPoints ?? []).map((dp) => ({
    period: format(new Date(dp.periodStart), "MMM"),
    value: dp.value,
  }));

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Management Dashboard"
        description="DORA metrics, team productivity, and business KPIs"
      >
        <select
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
          className="rounded-lg border border-slate-200/60 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </PageHeader>

      {/* DORA Metrics */}
      <section>
        <h2 className="text-section-title text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-indigo-500" />
          DORA Metrics
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Deploy Frequency"
            value={`${(dora?.deploymentFrequency ?? 0).toFixed(1)}/day`}
            trend={trendDirection(dora?.comparison, "deployment_frequency")}
          />
          <MetricCard
            label="Lead Time"
            value={
              dora?.leadTimeHours != null
                ? `${dora.leadTimeHours.toFixed(1)}h`
                : "N/A"
            }
            trend={trendDirection(dora?.comparison, "lead_time")}
            trendInverted
          />
          <MetricCard
            label="Change Failure Rate"
            value={`${(dora?.changeFailureRatePct ?? 0).toFixed(1)}%`}
            trend={trendDirection(dora?.comparison, "change_failure_rate")}
            trendInverted
          />
          <MetricCard
            label="MTTR"
            value={
              dora?.mttrHours != null
                ? `${dora.mttrHours.toFixed(1)}h`
                : "N/A"
            }
            trend={trendDirection(dora?.comparison, "mttr")}
            trendInverted
          />
        </div>

        {/* Deploy Frequency Trend */}
        {deployTrendData.length > 0 && (
          <div className="mt-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3">
              Deployment Frequency Trend
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={deployTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" fontSize={12} stroke="#94a3b8" />
                <YAxis fontSize={12} stroke="#94a3b8" />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#6366f1"
                  fill="#e0e7ff"
                  strokeWidth={2}
                  name="Deploys/day"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Productivity Metrics */}
      <section>
        <h2 className="text-section-title text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Team Productivity
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="PRs Merged"
            value={productivity?.prsMerged ?? 0}
            trend={trendDirection(productivity?.comparison, "prs_merged")}
          />
          <MetricCard
            label="Avg Review Time"
            value={
              productivity?.avgReviewTimeHours != null
                ? `${productivity.avgReviewTimeHours.toFixed(1)}h`
                : "N/A"
            }
            trend={trendDirection(
              productivity?.comparison,
              "avg_review_time"
            )}
            trendInverted
          />
          <MetricCard
            label="Total Commits"
            value={productivity?.totalCommits ?? 0}
            trend={trendDirection(productivity?.comparison, "total_commits")}
          />
          <MetricCard
            label="Active Contributors"
            value={productivity?.activeContributors ?? 0}
            trend={trendDirection(
              productivity?.comparison,
              "active_contributors"
            )}
          />
        </div>

        {/* PRs Merged Trend */}
        {prTrendData.length > 0 && (
          <div className="mt-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3">
              PRs Merged Trend
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={prTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" fontSize={12} stroke="#94a3b8" />
                <YAxis fontSize={12} stroke="#94a3b8" />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  name="PRs Merged"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Business Metrics */}
      <section>
        <h2 className="text-section-title text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          Business Impact
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Uptime"
            value={
              business?.uptimePct != null
                ? `${business.uptimePct.toFixed(2)}%`
                : "N/A"
            }
          />
          <MetricCard
            label="Incidents"
            value={business?.totalIncidents ?? 0}
            subtext={`${business?.incidentsResolved ?? 0} resolved`}
          />
          <MetricCard
            label="Cloud Cost"
            value={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(business?.totalCloudCost ?? 0)}
            trend={
              business?.costChangePct != null
                ? {
                    value: Math.abs(business.costChangePct),
                    direction:
                      business.costChangePct >= 0 ? "up" : "down",
                  }
                : undefined
            }
            trendInverted
          />
          <MetricCard
            label="New Services"
            value={business?.newServicesLaunched ?? 0}
          />
        </div>
      </section>

      {/* Engineering Analytics */}
      <section>
        <h2 className="text-section-title text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-rose-500" />
          Engineering Analytics
        </h2>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <MetricCard label="Total PRs" value={analytics?.total_prs_period ?? 0} />
          <MetricCard label="Merged PRs" value={analytics?.merged_prs_period ?? 0} />
          <MetricCard label="Hotfixes to Prod" value={analytics?.hotfixes_period ?? 0} />
          <MetricCard label="Zero-Review Merges" value={analytics?.zero_review_merges_period ?? 0} />
          <MetricCard label="Failed Builds" value={analytics?.failed_builds_period ?? 0} />
          <MetricCard label="Force Merges" value={analytics?.force_merges_period ?? 0} />
        </div>

        {/* PR Activity + Build Failures */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3">PR Activity by Author</h3>
            {(prActivity?.by_author?.length ?? 0) > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="pb-2 font-medium">Author</th>
                    <th className="pb-2 font-medium text-right">Total</th>
                    <th className="pb-2 font-medium text-right">Merged</th>
                    <th className="pb-2 font-medium text-right">Open</th>
                    <th className="pb-2 font-medium text-right">Avg Size</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {prActivity!.by_author.slice(0, 8).map((row) => (
                    <tr key={row.author_login} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-2 font-medium text-slate-700 dark:text-slate-300">{row.author_login}</td>
                      <td className="py-2 text-right text-slate-600 dark:text-slate-400">{row.total_prs}</td>
                      <td className="py-2 text-right text-emerald-600 dark:text-emerald-400">{row.merged_prs}</td>
                      <td className="py-2 text-right text-blue-600 dark:text-blue-400">{row.open_prs}</td>
                      <td className="py-2 text-right text-slate-500">{Math.round(row.avg_pr_size)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-400">No PR activity in this period.</p>
            )}
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3">Build Failures by Actor</h3>
            {(buildFailures?.length ?? 0) > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="pb-2 font-medium">Actor</th>
                    <th className="pb-2 font-medium text-right">Runs</th>
                    <th className="pb-2 font-medium text-right">Failures</th>
                    <th className="pb-2 font-medium text-right">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {buildFailures!.slice(0, 8).map((row) => (
                    <tr key={row.actor_login} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-2 font-medium text-slate-700 dark:text-slate-300">{row.actor_login}</td>
                      <td className="py-2 text-right text-slate-600 dark:text-slate-400">{row.total_runs}</td>
                      <td className="py-2 text-right text-rose-600 dark:text-rose-400">{row.failures}</td>
                      <td className="py-2 text-right">
                        <span className={clsx(
                          "font-medium",
                          row.failure_rate > 50 ? "text-rose-600 dark:text-rose-400"
                            : row.failure_rate > 20 ? "text-amber-600 dark:text-amber-400"
                            : "text-emerald-600 dark:text-emerald-400"
                        )}>
                          {row.failure_rate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-400">No build failures in this period.</p>
            )}
          </div>
        </div>

        {/* Risk tables */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Hotfixes */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
              Hotfixes to Prod ({hotfixes?.length ?? 0})
            </h3>
            {(hotfixes?.length ?? 0) > 0 ? (
              <ul className="space-y-2">
                {hotfixes!.slice(0, 5).map((pr) => (
                  <li key={pr.id} className="flex items-start gap-2 text-sm">
                    <span className="text-slate-400 shrink-0">#{pr.github_pr_number}</span>
                    <span className="flex-1 text-slate-700 dark:text-slate-300 truncate">{pr.title}</span>
                    {pr.html_url && (
                      <a href={pr.html_url} target="_blank" rel="noreferrer" className="shrink-0 text-slate-400 hover:text-brand-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                No hotfixes this period
              </p>
            )}
          </div>

          {/* Zero-review merges */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
              Zero-Review Merges ({zeroReviews?.length ?? 0})
            </h3>
            {(zeroReviews?.length ?? 0) > 0 ? (
              <ul className="space-y-2">
                {zeroReviews!.slice(0, 5).map((pr) => (
                  <li key={pr.id} className="flex items-start gap-2 text-sm">
                    <span className="text-slate-400 shrink-0">#{pr.github_pr_number}</span>
                    <span className="flex-1 text-slate-700 dark:text-slate-300 truncate">{pr.title}</span>
                    {pr.html_url && (
                      <a href={pr.html_url} target="_blank" rel="noreferrer" className="shrink-0 text-slate-400 hover:text-brand-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                All PRs reviewed
              </p>
            )}
          </div>

          {/* Force merges */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card p-6">
            <h3 className="text-card-title text-slate-600 dark:text-slate-400 mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
              Force Merges ({forceMerges?.length ?? 0})
            </h3>
            {(forceMerges?.length ?? 0) > 0 ? (
              <ul className="space-y-2">
                {forceMerges!.slice(0, 5).map((pr) => (
                  <li key={pr.id} className="flex items-start gap-2 text-sm">
                    <span className="text-slate-400 shrink-0">#{pr.github_pr_number}</span>
                    <span className="flex-1 text-slate-700 dark:text-slate-300 truncate">{pr.title}</span>
                    {pr.html_url && (
                      <a href={pr.html_url} target="_blank" rel="noreferrer" className="shrink-0 text-slate-400 hover:text-brand-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                No force merges detected
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
